import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { injectTourTheme } from '../../../features/onboarding/config/tourTheme';

const useContractTour = (isExternal, contractLoaded) => {
  const { t } = useTranslation();
  const tourRef = useRef(null);
  const themeMode = useSelector((state) => state.theme?.mode || 'light');

  useEffect(() => {
    // Only trigger for external users who haven't completed the tour
    if (!isExternal || !contractLoaded) return;
    if (localStorage.getItem('contract_tour_completed') === 'true') return;

    // Inject shared theme CSS
    injectTourTheme(themeMode);

    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => {
      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          scrollTo: false,
          cancelIcon: { enabled: true },
          classes: 'lawis-tour-step',
        },
      });

      tour.addStep({
        id: 'editor-intro',
        text: `<div class="shepherd-step-counter">1 / 4</div>
               <p>${t('tour.step1.content', "This is the contract shared with you. Read through it carefully before adding your feedback.")}</p>`,
        title: t('tour.step1.title', 'Contract Editor'),
        attachTo: { element: '.editor-container', on: 'right' },
        buttons: [
          { text: t('tour.skip', 'Skip'), action: tour.complete, classes: 'shepherd-button-secondary' },
          { text: t('tour.next', 'Next'), action: tour.next, classes: 'shepherd-button-primary' },
        ],
      });

      tour.addStep({
        id: 'selection-bubble',
        text: `<div class="shepherd-step-counter">2 / 4</div>
               <p>${t('tour.step2.content', "Select any text to add a comment or a suggestion. Your feedback will be visible to all collaborators.")}</p>`,
        title: t('tour.step2.title', 'Add Comments & Suggestions'),
        attachTo: { element: '.editor-body', on: 'right' },
        buttons: [
          { text: t('tour.back', 'Back'), action: tour.back, classes: 'shepherd-button-secondary' },
          { text: t('tour.next', 'Next'), action: tour.next, classes: 'shepherd-button-primary' },
        ],
      });

      tour.addStep({
        id: 'right-panel',
        text: `<div class="shepherd-step-counter">3 / 4</div>
               <p>${t('tour.step3.content', "Explore Lawis features: AI analysis, risk detection, clause extraction, and more. Start your free trial to unlock them.")}</p>`,
        title: t('tour.step3.title', 'Discover Lawis Features'),
        attachTo: { element: '.MuiPaper-root > .MuiBox-root:last-child', on: 'left' },
        buttons: [
          { text: t('tour.back', 'Back'), action: tour.back, classes: 'shepherd-button-secondary' },
          { text: t('tour.next', 'Next'), action: tour.next, classes: 'shepherd-button-primary' },
        ],
      });

      tour.addStep({
        id: 'done',
        text: `<div class="shepherd-step-counter">4 / 4</div>
               <p>${t('tour.step4.content', "You're all set! Start reviewing the contract and share your feedback.")}</p>`,
        title: t('tour.step4.title', "You're ready!"),
        buttons: [
          { text: t('tour.done', 'Start reviewing'), action: tour.complete, classes: 'shepherd-button-primary' },
        ],
      });

      tour.on('complete', () => {
        localStorage.setItem('contract_tour_completed', 'true');
      });

      tour.on('cancel', () => {
        localStorage.setItem('contract_tour_completed', 'true');
      });

      tourRef.current = tour;
      tour.start();
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (tourRef.current) {
        tourRef.current.cancel();
        tourRef.current = null;
      }
    };
  }, [isExternal, contractLoaded, t]);

  return tourRef;
};

export default useContractTour;
